import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { docTypeLabel, type Doc } from '@/lib/documents';
import { useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { Card, PageHeader, Spinner } from '@/components/ui/misc';
import { DocumentList } from '@/components/documents/DocumentList';
import { TermsExtract } from '@/components/ai/Extract';

export function DocumentDetail() {
  const program = useOutletContext<Program>();
  const { documentId = '' } = useParams();
  const q = useQuery({
    queryKey: ['documents', program.id, 'one', documentId],
    queryFn: () => api.get<Doc>(`/api/programs/${program.id}/documents/${documentId}`),
  });
  const tracker = useTracker(program.id);
  if (q.isLoading) return <Spinner className="h-full" />;
  if (!q.data) return <p className="p-8 text-sm text-muted">{errorMessage(q.error)}</p>;
  const d = q.data;
  const task = tracker.data?.tasks.find((t) => t.id === d.taskId);
  const del = tracker.data?.deliverables.find((x) => x.id === d.deliverableId);
  const canUpload = program.capabilities.includes('plan.manage');
  return (
    <div className="mx-auto max-w-3xl px-6 py-5">
      <Link
        to={`/p/${program.id}/documents`}
        className="mb-3 inline-flex items-center gap-1 text-xs text-muted hover:text-text"
      >
        <ArrowLeft className="size-3.5" /> Documents
      </Link>
      <PageHeader
        title={d.title}
        description={`${docTypeLabel(d.type, program)}${d.tags.length ? ` · ${d.tags.join(', ')}` : ''}`}
        actions={
          canUpload && d.versions.length > 0 && d.type !== 'PUBLISHED_REPORT' ? (
            <TermsExtract program={program} documentId={d.id} documentTitle={d.title} documentType={d.type} />
          ) : undefined
        }
      />
      <Card className="px-4 py-1">
        <DocumentList program={program} docs={[d]} canUpload={canUpload} />
      </Card>
      <p className="mt-3 text-[13px] text-muted">
        {del && (
          <>
            Filed for the deliverable{' '}
            <Link className="underline" to={`/p/${program.id}/deliverables/${del.id}`}>
              {del.name}
            </Link>
            .{' '}
          </>
        )}
        {task && (
          <>
            Filed on the task{' '}
            <Link className="underline" to={`/p/${program.id}/tracker?task=${task.id}`}>
              {task.title}
            </Link>
            .
          </>
        )}
      </p>
    </div>
  );
}
